import { EXTERNAL_SYNC_PROVIDERS } from '../constants/external-sync.constants';

/**
 * Calculate the numbering for each item in the tree
 * @param {{index: number, level: number}[]} items
 * @returns {string[]} the numbering for each item in the tree
 */
function numberingForHeading(items) {
  const createTree = items => {
    const root = { index: 0, level: 0, numbering: '', parent: null };
    const tree = [root, ...items];

    tree.forEach((item, index) => {
      if (index === 0) return; // Skip root node
      const parent = tree
        .slice(0, index)
        .reverse()
        .find(i => i.level < item.level);

      item.parent = parent.index;
    });

    return tree;
  };

  const numbering = (item, j) => {
    if (item.parent === null) return;
    if (item.parent === 0) item.numbering = `${j + 1}`;
    else {
      const parent = tree.find(i => i.index === item.parent);
      item.numbering = `${parent.numbering}.${j + 1}`;
    }
  };

  // Goes over the tree in DFS to calculate the numbering
  const dfs = node => {
    tree
      .filter(item => item.parent === node.index)
      .forEach((item, i) => {
        numbering(item, i);
        dfs(item);
      });
  };

  const tree = createTree(items);
  dfs(tree[0]); // start from the root
  tree.shift(); // remove the root
  return tree.map(item => item.numbering);
}

const EXTRACTABLE_EXTENSIONS = new Set(['.pdf', '.doc', '.docx', '.ppt', '.pptx']);

const EXTRACTABLE_KNOWLEDGE_TYPES = new Set(['pdf', 'document', 'content']);

/**
 * Check if a file supports metadata extraction based on its extension.
 * Works with upload file objects that have fileName or c8Path properties.
 */
function isMetadataExtractableByExtension(file) {
  const name = file.fileName || file.c8Path || '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return false;
  const ext = name.substring(dotIndex).toLowerCase();
  return EXTRACTABLE_EXTENSIONS.has(ext);
}

/**
 * Check if a knowledge item supports metadata extraction based on its type.
 */
function isMetadataExtractableByType(type) {
  return EXTRACTABLE_KNOWLEDGE_TYPES.has(type);
}

/**
 * Content synced from an external provider (SharePoint, PolicyTech, …) is a one-way
 * mirror of a system-of-record, so it is read-only in the client — except Labels
 * (org locations) and Share to Hub, which are C8-side classification. Gate on the
 * presence of the provider so any future provider is covered automatically.
 */
function isExternalSyncReadOnly(options) {
  return !!options?.externalSyncProvider;
}

/**
 * PolicyTech mirrors the whole workspace structure, so *creating* sections/binders/
 * directories is blocked for it specifically. SharePoint only syncs items into a
 * binder and leaves the surrounding structure user-managed, so it doesn't block
 * category creation.
 */
function isPolicyTechSynced(options) {
  return options?.externalSyncProvider === EXTERNAL_SYNC_PROVIDERS.POLICYTECH;
}

/**
 * The update payload allowed on externally-synced content: only the user-editable
 * fields (labels, Share to Hub, widget visibility). The backend enforces the
 * server-owned ones from the stored document and merges `options`, so the provider
 * survives. Untouched keys are omitted rather than sent empty — a Share-to-Hub-only
 * save must not wipe the item's labels.
 */
function buildSyncedUpdatePayload({ id, labels, shareToHub, hideInWidget }) {
  const payload = { id };

  if (labels !== undefined) payload.labels = labels;
  if (shareToHub !== undefined) payload.shareToHub = shareToHub;
  if (hideInWidget !== undefined) payload.options = { hideInWidget };

  return payload;
}

export {
  numberingForHeading,
  EXTRACTABLE_EXTENSIONS,
  EXTRACTABLE_KNOWLEDGE_TYPES,
  isMetadataExtractableByExtension,
  isMetadataExtractableByType,
  isExternalSyncReadOnly,
  isPolicyTechSynced,
  buildSyncedUpdatePayload,
};
